import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment-timezone";
import Tooltip from "../../../../../components/shared/Tooltip";
import Avatar from "../../../../../components/shared/Avatar";
import SimpleTable from "../../../../../components/SimpleTable";
import OpenRequestIndicator from "../OpenRequestIndicator";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { usePermissions } from "../../hooks/usePermissions";
import { getStatusColor } from "../../helpers/eventdisplay/getStatusColor";
import { useCompanyCurrency, useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { currencyFormatter } from "../../../../../utils/helpers/currencyFormatter";
import { firstToUpper } from "../../../../../utils/helpers/string";
import { renderUserName } from "../../../../../utils/helpers/user";
import { renderSettingsResourceLabel } from "../../../../../utils/helpers/settings";
import { getOpenRequestMessage, hasOpenRequest } from "../../helpers/eventdisplay/hasOpenRequest";
import { cn } from "../../../../../lib/utils";

const columnsHelper = createColumnHelper();

function ShiftTooltip({ children, data, side = "top", isOpen }: any) {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();
    const dateFormat = useCompanyDateFormat();
    const { type } = useScheduleContext();
    const isTeam = ["team", "template", "profile"].includes(type);
    const { canViewRates } = usePermissions();

    const getColumns = useCallback((startDate: any) => {
        const columns = [
            columnsHelper.accessor("start", {
                header: t("start"),
                cell: (info) => {
                    const value = info.getValue();
                    const start = moment(startDate).add(value, "seconds");
                    return moment(start).format("hh:mm A")
                }
            }),
            columnsHelper.accessor("end", {
                header: t("end"),
                cell: (info) => {
                    const value = info.getValue();
                    const end = moment(startDate).add(value, "seconds");
                    return moment(end).format("hh:mm A")
                }
            }),
            columnsHelper.accessor("payCode", {
                header: t("type"),
                cell: (info: any) => {
                    const value = info.getValue();
                    return value && value?.code;
                }
            }),
            columnsHelper.accessor("location", {
                header: t("location"),
                cell: (info: any) => {
                    const value = info.getValue();
                    return value && value?.code;
                }
            }),
            columnsHelper.accessor("job", {
                header: t("job"),
                cell: (info: any) => {
                    const value = info.getValue();
                    return value && value?.code;
                }
            }),
            columnsHelper.accessor("duration", {
                header: t("duration"),
                cell: (info) => {
                    const value = info.getValue();
                    return renderDurationAsFormat(value || 0, "HH:mm")
                }
            }),
        ];
        if (canViewRates || !isTeam) {
            columns.push(columnsHelper.accessor("total", {
                header: t("total"),
                cell: ({ row: { original } }: any) => {
                    return currencyFormatter(original?.total || 0, original?.job?.currency || currency)
                }
            }));
        }
        return columns;
    }, [currency, canViewRates, isTeam, t]);

    return (
        <Tooltip
            open={isOpen}
            trigger={children}
            contentClassName="tw-bg-white tw-text-neutral-900 tw-shadow-md scheduler-tooltip"
            side={side}
        >
            <div className="tw-p-3">
                {hasOpenRequest(data) && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-px-2 tw-py-1 tw-rounded-md tw-bg-amber-50 tw-text-amber-700 tw-text-sm tw-font-medium">
                        <OpenRequestIndicator event={data} />
                        {t(getOpenRequestMessage(data))}
                    </div>
                )}

                {data?.type === "open-shift" && (
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-mb-3">
                        <h2 className="tw-font-bold tw-text-sm">
                            {firstToUpper(t("open-shift"))}
                        </h2>
                        <div className="tw-text-primary tw-text-sm tw-flex tw-items-center tw-gap-1">
                            <Avatar user={data?.user} size="sm" />
                            {renderUserName(data?.user)}
                        </div>
                    </div>
                )}

                {(isTeam && data?.type !== "unassigned") && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                        <Avatar
                            user={data?.resource}
                            size="lg"
                        />
                        <span>
                            {renderUserName(data?.resource)}
                        </span>
                    </div>
                )}

                {data?.type === "unassigned" && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                        <h2 className="tw-font-bold tw-text-sm">
                            {t("unassigned-shift")}
                        </h2>
                        <h2 className="tw-text-sm">{"("}{data?.slots || 0}{")"}</h2>
                    </div>
                )}

                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <h4 >{moment(data?.startDate).format(dateFormat)}</h4>
                    {!["unassigned", "open-shifts"].includes(data?.type) && (
                        <span className={cn("tw-font-bold", getStatusColor(data?.status))}>
                            {t(data?.status)}
                        </span>
                    )}
                    <h4 >{renderSettingsResourceLabel(data?.sections?.[0]?.payCode)}</h4>
                </div>

                <SimpleTable
                    data={data?.sections || []}
                    columns={getColumns(data?.startDate)}
                    enableRowSelection={false}
                    footer={false}
                    loading={undefined}
                    getRowProps={undefined}
                />
            </div>
        </Tooltip>
    );
}

export default ShiftTooltip;
